import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Dialog, { useDialog } from 'components/organization/dialog/dialog';

type PromiseNumber = number | PromiseLike<number>;

const useConfirm = () => {
  const { closeDialog, openDialog, ref } = useDialog();

  const resolveRef = useRef<((result: PromiseNumber) => void) | null>(null);
  const [content, setContent] = useState<string[]>([]);

  const createPromise: () => [PromiseNumber, (arg: PromiseNumber) => void] = useMemo(() => {
    let resolver: (value: PromiseNumber) => void;
    return () => [new Promise(resolve => (resolver = resolve)), resolver];
  }, []);

  const getConfirmation = useCallback(
    async (title: string, contentText: string, ...args: string[]) => {
      setContent([title, contentText, ...args]);
      const [promise, resolve] = createPromise();
      resolveRef.current = resolve;
      return promise;
    },
    [createPromise]
  );

  const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(Number(e.currentTarget.value));
    setContent([]);
  }, []);

  /* external mouse event immediately closing the dialog without this open/close logic */
  useEffect(() => {
    if (!ref.current?.open && content.length) {
      openDialog();
    } else if (ref.current?.open && !content.length) {
      closeDialog();
    }
  }, [closeDialog, content, openDialog, ref]);

  const ConfirmationDialog = useCallback(
    ({ danger }: { danger?: boolean }) => (
      <Dialog closeDialog={closeDialog} ref={ref} data-cy="confirmation-dialog">
        <div className="grid-1 auto-rows">
          <h3 className="text-info-dark font-weight-medium">{content[0]}</h3>
          <span className="white-space-wrap">{content[1]}</span>
          <div className="grid-2 justify-self-end min-width-fit w-50">
            <button className="btn-outline" type="button" value={0} onClick={handleClick} data-cy="cancel-button">
              {content[2] ?? 'NO'}
            </button>
            <button
              className={`btn-${danger ? 'danger' : 'primary'}`}
              type="button"
              value={1}
              onClick={handleClick}
              data-cy="confirm-button"
            >
              {content[3] ?? 'YES'}
            </button>
          </div>
        </div>
      </Dialog>
    ),
    [closeDialog, content, ref, handleClick]
  );

  return { getConfirmation, ConfirmationDialog };
};

export default useConfirm;
